/**
 * @module app.hl7
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MessageService } from 'app/hl7/services/message-service';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import * as HL7 from 'app/hl7/models/essette-api';

@autoinject()
export class MemberHl7Messages extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    public memberHl7Messages: ReadonlyArray<HL7.Message> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MessageService,
        private readonly transitional: TransitionalManager,
        ...rest: any[]) {
        super(...rest);

        // Custom Sort
        this.sorting.entries = [
            {
                reference: 'header.messageDateTime',
                direction: SortDirection.Descending
            }
        ];
    }

    ////****************** Events ***************************************************////
    private async reload(): Promise<void> {
        await this.loadMemberHl7Messages();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'header.messageDateTime',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ***********************************************////
    protected openSummary(id: string): void {
        this.transitional.openHL7MessageDetailPopup(id);
    }

    ////****************** Service Calls *******************************************////
    @apiCall
    private async loadMemberHl7Messages(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'type'
        ];

        const fields = [
            'header.messageControlID',
            'header.messageDateTime',
            'header.sendingApplication',
            'header.sendingFacility',
            'metadata.created.timestamp',
            'type.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadMessagesByMember(this.memberId, options, includes, fields);
            this.memberHl7Messages = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
